import { defineComponent, useAttrs, computed, createBlock, openBlock, resolveDynamicComponent, mergeProps, withCtx, createElementVNode, createCommentVNode, normalizeClass, renderSlot } from "vue";
import { S as SIZES } from "./p-btn.types.js";
import { _ as _sfc_main$1 } from "./p-icon.js";
import { _ as _sfc_main$2 } from "./p-ring-loader.js";
import { isExternalLink } from "../link.js";
import { sanitizeUrl } from "../sanitization.js";
import { c as ce } from "./index.js";
const _hoisted_1 = { class: "slot-wrapper empty:hidden" };
const btnClasses = {
  slots: {
    button: "relative inline-block whitespace-nowrap rounded font-medium outline-none disabled:pointer-events-none disabled:cursor-default disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:cursor-default aria-disabled:opacity-50",
    content: "flex items-center justify-center has-[.slot-wrapper:empty]:gap-0",
    loader: "absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center font-medium",
    icon: "shrink-0"
  },
  variants: {
    type: {
      primary: {
        button: "bg-primary text-surface hover:bg-accent active:bg-p-blue-80",
        loader: "text-surface"
      },
      secondary: {
        button: "bg-p-gray-20 hover:bg-p-gray-30 active:bg-p-gray-40",
        loader: "text-p-purple-60"
      },
      "primary-outline": {
        button: "bg-p-blue-10 text-p-purple-60 ring-1 ring-inset ring-p-purple-60 hover:bg-p-gray-20",
        loader: "text-p-purple-60"
      },
      "secondary-outline": {
        button: "bg-surface text-p-purple-60 ring-1 ring-inset ring-p-gray-30 hover:bg-p-blue-10 aria-selected:bg-p-blue-10",
        loader: "text-p-purple-60"
      },
      "secondary-outline-blue": {
        button: "bg-surface text-p-purple-60 ring-1 ring-inset ring-p-gray-30 hover:bg-p-blue-10 aria-selected:bg-p-blue-15 aria-selected:text-p-blue-60",
        loader: "text-p-purple-60"
      },
      error: { button: "bg-p-red-40 text-white hover:bg-p-red-50", loader: "text-white" },
      success: { button: "bg-p-green-40 text-white hover:bg-p-green-50", loader: "text-white" },
      "primary-link": { button: "bg-transparent text-primary underline hover:text-accent", loader: "text-p-blue-60" },
      "secondary-ghost": { button: "text-on-surface hover:bg-p-gray-20", loader: "text-p-purple-60" },
      "secondary-ghost-dark": { button: "text-white hover:bg-p-purple-50", loader: "text-p-blue-15" }
    },
    size: {
      sm: {
        button: "px-3 has-[.slot-wrapper:empty]:px-1.5 py-1.5 text-sm leading-5 h-8",
        content: "gap-1",
        icon: "text-base p-0.5"
      },
      md: {
        button: "px-6 has-[.slot-wrapper:empty]:px-2.5 has-[.slot-wrapper:empty]:py-2.5 py-2 text-base h-10",
        content: "gap-2",
        icon: "text-xl"
      },
      lg: {
        button: "px-6 has-[.slot-wrapper:empty]:px-3 py-3 text-lg leading-6 h-12",
        content: "gap-2.5",
        icon: "text-2xl"
      }
    }
  },
  defaultVariants: {
    type: "primary",
    size: "md"
  }
};
const btn = ce(btnClasses);
const BUTTON_TYPES = Object.keys(btnClasses.variants.type);
const BUTTON_NATIVE_TYPES = ["button", "submit", "reset"];
const LOADER_SIZES = { sm: 24, md: 30, lg: 40 };
const _sfc_main = /* @__PURE__ */ defineComponent({
  ...{
    name: "PBtn"
  },
  __name: "p-btn",
  props: {
    /**
     * The button style e.g primary, secondary, primary-outline, secondary-outline, error, success, primary-link
     */
    type: {
      type: String,
      default: "primary",
      validator(value) {
        return BUTTON_TYPES.includes(value);
      }
    },
    /**
     * The button native type e.g button, submit, reset
     */
    nativeType: {
      type: String,
      default: "button",
      validator(value) {
        return BUTTON_NATIVE_TYPES.includes(value);
      }
    },
    /**
     * The button size e.g sm, md, lg
     */
    size: {
      type: String,
      default: "md",
      validator(value) {
        return SIZES.includes(value);
      }
    },
    /**
     * Whether the button is loading
     */
    loading: {
      type: Boolean,
      default: false
    },
    /**
     * Whether the button is selected (sets aria-selected attribute)
     */
    selected: {
      type: Boolean,
      default: false
    },
    /**
     * Exactly as the `to` prop in `RouterLink`, when used, it renders a link that triggers a navigation on click.
     * See https://router.vuejs.org/api/#RouteLocationRaw
     */
    to: {
      type: [Object, String],
      default: ""
    },
    /**
     * The icon to display on the button
     */
    icon: {
      type: String,
      default: ""
    },
    iconRight: {
      type: String,
      default: ""
    }
  },
  setup(__props) {
    const attrs = useAttrs();
    const props = __props;
    const classes = computed(() => {
      const { button, content, loader, icon } = btn({
        type: props.type,
        size: props.size
      });
      return { button: button(), content: content(), loader: loader(), icon: icon() };
    });
    const isExternal = computed(() => isExternalLink(String(props.to)));
    const btnAttrs = computed(() => {
      const res = {
        target: isExternal.value ? "_blank" : null,
        to: !isExternal.value && props.to ? props.to : null,
        type: props.to ? null : props.nativeType,
        disabled: attrs.disabled || props.loading ? "" : null,
        "aria-selected": !isExternal.value ? props.selected : false,
        "aria-disabled": attrs.disabled || props.loading
      };
      if (isExternal.value) {
        res.href = sanitizeUrl(String(props.to));
      }
      return res;
    });
    return (_ctx, _cache) => {
      return openBlock(), createBlock(resolveDynamicComponent(isExternal.value ? "a" : __props.to ? "RouterLink" : "button"), mergeProps(btnAttrs.value, {
        class: classes.value.button
      }), {
        default: withCtx(() => [
          createElementVNode("div", {
            class: normalizeClass([{ invisible: __props.loading }, classes.value.content])
          }, [
            __props.icon ? (openBlock(), createBlock(_sfc_main$1, {
              key: 0,
              icon: __props.icon,
              class: normalizeClass(classes.value.icon)
            }, null, 8, ["icon", "class"])) : createCommentVNode("", true),
            createElementVNode("span", _hoisted_1, [
              renderSlot(_ctx.$slots, "default")
            ]),
            __props.iconRight ? (openBlock(), createBlock(_sfc_main$1, {
              key: 1,
              icon: __props.iconRight,
              class: normalizeClass(classes.value.icon)
            }, null, 8, ["icon", "class"])) : createCommentVNode("", true)
          ], 2),
          __props.loading ? (openBlock(), createBlock(_sfc_main$2, {
            key: 0,
            size: LOADER_SIZES[props.size],
            class: normalizeClass(classes.value.loader)
          }, null, 8, ["size", "class"])) : createCommentVNode("", true)
        ]),
        _: 3
      }, 16, ["class"]);
    };
  }
});
export {
  _sfc_main as _
};
